/* eslint-disable jsx-a11y/label-has-associated-control */

import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Button } from 'react-bootstrap';
import DateRangePicker from 'common/date-range-picker';
import AnalyticsChartContainer, { getDefaultChartOptions } from 'common/analytics-chart-container';
import moment from 'moment';
import axios from 'axios';
import { LMS_BASE_URL } from 'config';
import 'utils/chartSetup';
import {
  MOCK_LOGIN_RESPONSE,
  USE_ANALYTICS_MOCK_DATA,
} from 'analytics/analyticsMockData';

const buildLoginGraphData = (data) => {
  const labels = data?.labels || [];
  const loginValues = data?.login_data;

  let series = [];
  if (Array.isArray(loginValues)) {
    series = loginValues;
  } else if (loginValues && typeof loginValues === 'object') {
    series = labels.map((label) => loginValues[label] ?? 0);
  }

  return {
    labels,
    datasets: [{
      label: 'Number of Learners',
      data: series,
      fill: false,
      backgroundColor: 'rgb(57, 91, 135)',
      borderColor: 'rgba(57, 91, 135, 0.7)',
      borderWidth: 3,
    }],
  };
};

const LearnerLoginGraph = () => {
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days'),
    endDate: moment(),
  });
  const [granularity, setGranularity] = useState('days');
  const [graphData, setGraphData] = useState(() => buildLoginGraphData(
    USE_ANALYTICS_MOCK_DATA ? MOCK_LOGIN_RESPONSE : {},
  ));

  const fetchLoginData = async (start, end, gran) => {
    if (USE_ANALYTICS_MOCK_DATA) {
      setGraphData(buildLoginGraphData(MOCK_LOGIN_RESPONSE));
      return;
    }

    try {
      const { data } = await axios({
        withCredentials: true,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': localStorage.getItem('lms-csrf-token'),
        },
        url: `${LMS_BASE_URL}adminpanel/analytics/login/`,
        params: {
          startDate: start.format('YYYY-MM-DD'),
          endDate: end.format('YYYY-MM-DD'),
          granularity: gran,
        },
      });

      setGraphData(buildLoginGraphData(data));
    } catch (error) {
      setGraphData(buildLoginGraphData({}));
    }
  };

  useEffect(() => {
    fetchLoginData(dateRange.startDate, dateRange.endDate, granularity);
  }, [dateRange, granularity]);

  const handleApply = (event, picker) => {
    setDateRange({
      startDate: picker.startDate,
      endDate: picker.endDate,
    });
  };

  const downloadCSV = () => {
    if (!graphData || !graphData.labels.length) return;

    const timestamp = moment().format('YYYY-MM-DD_HH-mm');
    const header = ['Date Granularity', ...graphData.datasets.map((ds) => ds.label)].join(',');
    const rows = graphData.labels.map((label, index) => [
      label, ...graphData.datasets.map((ds) => ds.data[index]),
    ].join(','));

    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `login_stats_${granularity}_${timestamp}.csv`;
    link.click();
  };

  if (!USE_ANALYTICS_MOCK_DATA && graphData.labels.length === 0) {
    return null;
  }

  return (
    <div className="analytics-chart-block">
      <h2>Login Stats</h2>

      <div className="analytics-section">
        <div className="analytics-chart-controls">
          <div>
            <label><strong>Date Range:</strong></label>
            <DateRangePicker
              initialSettings={{
                startDate: dateRange.startDate.toDate(),
                endDate: dateRange.endDate.toDate(),
                maxDate: moment().toDate(),
                ranges: {
                  Today: [moment(), moment()],
                  Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                  'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                  'Last 14 Days': [moment().subtract(13, 'days'), moment()],
                  'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                  'This Month': [moment().startOf('month'), moment().endOf('month')],
                  'Last Month': [
                    moment().subtract(1, 'month').startOf('month'),
                    moment().subtract(1, 'month').endOf('month'),
                  ],
                  'Last 3 Months': [moment().subtract(3, 'month'), moment()],
                  'Last 6 Months': [moment().subtract(6, 'month'), moment()],
                  'Last Year': [moment().subtract(1, 'year'), moment()],
                },
              }}
              onApply={handleApply}
            >
              <input
                type="text"
                className="form-control"
                value={
                  `${dateRange.startDate.format('MM/DD/YYYY')} - ${dateRange.endDate.format('MM/DD/YYYY')}`
                }
                readOnly
              />
            </DateRangePicker>
          </div>

          <div>
            <label><strong>Granularity:</strong></label>
            <select className="form-control" value={granularity} onChange={(e) => setGranularity(e.target.value)}>
              <option value="days">Days</option>
              <option value="months">Months</option>
              <option value="quarters">Quarters</option>
            </select>
          </div>

          <Button variant="primary" onClick={downloadCSV} disabled={!graphData?.labels?.length}>
            <span className="icon-Download-Outline-Color icon" /> Download Report
          </Button>
        </div>

        <AnalyticsChartContainer>
          {({ width, height }) => (
            <Line
              key={`${Math.round(width)}x${Math.round(height)}`}
              width={width}
              height={height}
              data={graphData}
              options={getDefaultChartOptions({
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { boxWidth: 12, padding: 15 },
                  },
                  tooltip: {
                    mode: 'index',
                    intersect: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { precision: 0 },
                    title: { display: true, text: 'Logins' },
                  },
                },
              })}
            />
          )}
        </AnalyticsChartContainer>
      </div>
    </div>
  );
};

export default LearnerLoginGraph;
